import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import ProjectEditor from "../components/ProjectEditor";
import { fetchLayouts, createLayout } from "../api/layout";
import { useDarkModeSetting } from "../hooks/useDarkModeSetting";
import { trackEvent } from "../utils/analytics";
import {
  getRandomLayoutTemplate,
  LAYOUT_TEMPLATES,
} from "../constants/layoutTemplates";
import { findStoryboardTemplate } from "../constants/storyboardTemplates";

/**
 * The editor. `kind="storyboard"` (at /storyboard/editor) opens the separate
 * storyboard project; `?template=<slug>` opens one of the board templates.
 */
function EditorPage({ kind = "comic" }) {
  const isStoryboard = kind === "storyboard";
  const [searchParams, setSearchParams] = useSearchParams();
  const templateSlug = isStoryboard ? searchParams.get("template") : null;
  // Read once: the project consumes it, then it leaves the URL
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seed = useMemo(() => findStoryboardTemplate(templateSlug)?.seed || null, []);
  const clearTemplateParam = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("template");
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [, setLayouts] = useState([]);
  const [activeLayout, setActiveLayout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(
    () => !localStorage.getItem("striply_tutorial_seen")
  );
  const darkMode = useDarkModeSetting();

  useEffect(() => {
    trackEvent("editor_session_start", { kind });
  }, [kind]);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchLayouts();

        if (data.length === 0) {
          const defaultLayout = await createDefaultLayout();
          setLayouts([defaultLayout]);
          setActiveLayout(defaultLayout);
        } else {
          setLayouts(data);
          setActiveLayout(data[0]);
        }
      } catch (error) {
        console.error("Failed to load layouts:", error);
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  async function createDefaultLayout() {
    const template =
      getRandomLayoutTemplate() || LAYOUT_TEMPLATES.PORTRAIT_THREE_BY_THREE;
    const {
      name,
      page: { width: pageWidth, height: pageHeight },
      margins,
      gutters,
      grid,
    } = template;

    const cells = [];
    for (let row = 0; row < grid.rows; row++) {
      for (let col = 0; col < grid.columns; col++) {
        cells.push({
          cell_key: `R${row + 1}C${col + 1}`,
          row_index: row,
          col_index: col,
          row_span: 1,
          col_span: 1,
          cell_type: "normal",
        });
      }
    }

    const layoutData = {
      name,
      page_width: pageWidth,
      page_height: pageHeight,
      margin_top: margins.top,
      margin_bottom: margins.bottom,
      margin_left: margins.left,
      margin_right: margins.right,
      gutter_horiz: gutters.horizontal,
      gutter_vert: gutters.vertical,
      cells,
    };

    return await createLayout(layoutData);
  }

  const dark = darkMode ? styles.dark : {};

  return (
    <div style={{ padding: 16, height: "100vh", display: "flex", flexDirection: "column", boxSizing: "border-box", background: darkMode ? "#191612" : "#f5f0e8" }}>
      <div style={styles.header}>
        <Link to="/" style={{ ...styles.logo, ...(darkMode ? { color: "#f5f0e8" } : {}) }}>striply</Link>
        <nav style={styles.nav}>
          {isStoryboard ? (
            <>
              <Link to="/storyboard-templates" style={{ ...styles.navLink, ...(darkMode ? { color: "#b8b0a0" } : {}) }}>Storyboard templates</Link>
              <Link to="/editor" style={{ ...styles.navLink, ...(darkMode ? { color: "#b8b0a0" } : {}) }}>Comic editor</Link>
            </>
          ) : (
            <Link to="/storyboard/editor" style={{ ...styles.navLink, ...(darkMode ? { color: "#b8b0a0" } : {}) }}>Storyboards</Link>
          )}
          <Link to="/about" style={{ ...styles.navLink, ...(darkMode ? { color: "#b8b0a0" } : {}) }}>About</Link>
          <Link to="/tutorial" style={{ ...styles.navLink, ...(darkMode ? { color: "#b8b0a0" } : {}) }}>Tutorial</Link>
        </nav>
      </div>
      {showBanner && (
        <div style={{ ...styles.banner, ...dark.banner }}>
          <span>New to Striply? Check out the tutorial for a quick walkthrough.</span>
          <span style={styles.bannerActions}>
            <a
              href="/tutorial#getting-started"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.bannerLink}
            >
              Open Tutorial
            </a>
            <button
              type="button"
              style={{ ...styles.bannerDismiss, ...dark.bannerDismiss }}
              onClick={() => {
                localStorage.setItem("striply_tutorial_seen", "1");
                setShowBanner(false);
                trackEvent("welcome_banner_dismissed");
              }}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </span>
        </div>
      )}
      {isLoading ? (
        <p>Loading...</p>
      ) : activeLayout ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ProjectEditor layout={activeLayout} kind={kind} seed={seed} onSeedUsed={clearTemplateParam} />
        </div>
      ) : (
        <p>No layout loaded yet.</p>
      )}
    </div>
  );
}

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  logo: {
    fontSize: 28,
    fontWeight: 700,
    color: "#1a1a1a",
    textDecoration: "none",
    fontFamily: "'Georgia', serif",
  },
  nav: {
    display: "flex",
    gap: 20,
  },
  navLink: {
    color: "#555",
    textDecoration: "none",
    fontSize: 15,
  },
  // A slip of paper on the desk, not a web alert
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    background: "#fdf6e3",
    border: "2px solid #1a1a1a",
    boxShadow: "3px 3px 0 rgba(0, 0, 0, 0.15)",
    marginBottom: 12,
    fontSize: 14,
    fontFamily: "'Georgia', serif",
    color: "#1a1a1a",
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  bannerLink: {
    color: "#00a4e4",
    fontWeight: 700,
    textDecoration: "none",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  bannerDismiss: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    color: "#1a1a1a",
    padding: "0 4px",
  },
  dark: {
    banner: {
      background: "#2a251e",
      border: "2px solid #4a4238",
      boxShadow: "3px 3px 0 rgba(0, 0, 0, 0.4)",
      color: "#e8e2d5",
    },
    bannerDismiss: {
      color: "#e8e2d5",
    },
  },
};

export default EditorPage;
